import { useState } from "react";
import { Select } from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  ExclamationOutlined,
  StarOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import "../../styles/aiInsights.css";
import AIObservationModal from "../../components/modals/AIObservationModal";

const CYCLE_OPTIONS = [
  {
    value: "all",
    label: "All",
  },
];

// Shaped like the expected API response so the page renders standalone.
const defaultInsights = {
  overall:
    "Customer interactions were generally positive, with strong product knowledge and good engagement. The most common gaps were in follow-up consistency and requirement understanding.",
  sections: [
    {
      key: "success",
      title: "What went well",
      subtitle: "Positive observations identified across your audits.",
      total: 18,
      unit: "observations",
      items: [
        {
          id: 1,
          text: "Good product knowledge and clear explanation of features and benefits.",
          audits: 6,
        },
        {
          id: 2,
          text: "Strong customer engagement and professional behaviour.",
          audits: 5,
        },
        {
          id: 3,
          text: "Clear explanation of project details and offerings.",
          audits: 4,
        },
        {
          id: 4,
          text: "Welcoming and helpful reception experience.",
          audits: 3,
        },
        {
          id: 5,
          text: "Smooth site visit experience and good hospitality at the location.",
          audits: 2,
        },
      ],
    },
    {
      key: "critical",
      title: "What didn't go well",
      subtitle: "Negative observations / gaps identified across your audits.",
      total: 11,
      unit: "observations",
      items: [
        {
          id: 1,
          text: "Follow-up was not consistent or not received within the expected time.",
          audits: 5,
        },
        {
          id: 2,
          text: "Customer requirements were not fully explored before presenting the product.",
          audits: 4,
        },
        {
          id: 3,
          text: "Pricing details were unclear or not communicated clearly.",
          audits: 3,
        },
        { id: 4, text: "Response time to enquiries was delayed.", audits: 2 },
        {
          id: 5,
          text: "Some key information (e.g. possession timeline, configurations) was missing.",
          audits: 2,
        },
      ],
    },
    {
      key: "warning",
      title: "What can be improved",
      subtitle:
        "Areas where the audit findings indicate an opportunity for improvement.",
      total: 7,
      unit: "areas",
      items: [
        {
          id: 1,
          text: "Improve follow-up consistency and ensure timely communication.",
          audits: 5,
        },
        {
          id: 2,
          text: "Strengthen requirement discovery and understanding.",
          audits: 4,
        },
        {
          id: 3,
          text: "Enhance pricing communication with clearer details.",
          audits: 3,
        },
        {
          id: 4,
          text: "Reduce response time to customer enquiries.",
          audits: 2,
        },
        {
          id: 5,
          text: "Ensure complete information is shared (e.g. possession, configurations).",
          audits: 2,
        },
      ],
    },
  ],
};

const SECTION_ICONS = {
  success: <CheckOutlined />,
  critical: <ExclamationOutlined />,
  warning: <StarOutlined />,
};

const SUMMARY_STATS = [
  { key: "success", label: "Positive observations", icon: <CheckOutlined /> },
  {
    key: "critical",
    label: "Negative observations",
    icon: <ExclamationOutlined />,
  },
  { key: "warning", label: "Improvement areas", icon: <StarOutlined /> },
];

const AIInsights = ({
  insights = defaultInsights,
  onViewAudits,
  onFiltersChange,
}) => {
  const [auditType, setAuditType] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);

  const sections = insights?.sections ?? [];
  const totals = Object.fromEntries(
    sections.map((s) => [s.key, s.total ?? s.items.length]),
  );

  const handleFilterChange = (value) => {
    setAuditType(value);
    onFiltersChange?.(value);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const getTitle = ({ type }) => {
    switch (type) {
      case "good":
        return "What went well";
      case "average":
        return "What didn't go well";
      case "bad":
        return "What can be improved";
      default:
        return "";
    }
  };

  const handleViewAudits = (section, item) => {
    onViewAudits?.(section.key, item);

    const typeMap = {
      success: "good",
      critical: "average",
      warning: "bad",
    };

    setModalType(typeMap[section.key]);
    setShowModal(true);
  };

  const handleHeaderCountClick = (type) => {
    const typeMap = {
      success: "good",
      critical: "average",
      warning: "bad",
    };

    setModalType(typeMap[type]);
    setShowModal(true);
  };

  return (
    <div className="aii-page">
      <header className="aii-header">
        <div className="aii-title-wrap">
          <div className="aii-spark-lg">
            <span className="geminiSparkle">✦</span>
          </div>
          <div>
            <h2 className="aii-title">AI Insights</h2>
            <p className="aii-subtitle">
              Understand the overall customer experience across your audits.
            </p>
          </div>
        </div>

        <div className="aii-filter">
          <AppstoreOutlined className="aii-filter-icon" />
          <span className="aii-filter-label">Type of Audit</span>
          <Select
            className="aii-filter-select"
            value={auditType}
            options={CYCLE_OPTIONS}
            onChange={handleFilterChange}
            popupMatchSelectWidth={false}
          />
          <span className="aii-filter-updated">Last updated 2 days ago</span>
        </div>
      </header>

      <section className="aii-overview">
        <div className="aii-overview-text">
          <div className="aii-spark-sm">
            <span className="geminiSparkle">✦</span>
          </div>
          <div>
            <h3 className="aii-overview-title">Overall Observation</h3>
            <p className="aii-overview-desc">{insights?.overall}</p>
          </div>
        </div>

        <div className="aii-stats">
          {SUMMARY_STATS.map((stat) => (
            <div
              className="aii-stat"
              key={stat.key}
              onClick={() => {
                handleHeaderCountClick(stat.key);
              }}
            >
              <div className={`aii-stat-icon ${stat.key}`}>{stat.icon}</div>
              <div>
                <div className={`aii-stat-count ${stat.key}`}>
                  {totals[stat.key] ?? 0}
                </div>
                <div className="aii-stat-label">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="aii-grid">
        {sections.map((section) => (
          <section className={`aii-card ${section.key}`} key={section.key}>
            <div className="aii-card-head">
              <div className={`aii-card-icon ${section.key}`}>
                {SECTION_ICONS[section.key]}
              </div>
              <div className="aii-card-head-text">
                <h3 className="aii-card-title">{section.title}</h3>
                <p className="aii-card-sub">{section.subtitle}</p>
              </div>
              <span className={`aii-pill ${section.key}`}>
                {section.total} {section.unit}
              </span>
            </div>

            <ul className="aii-list">
              {section.items.map((item) => (
                <li className="aii-item" key={item.id}>
                  <p className="aii-item-text">{item.text}</p>
                  <div className="aii-item-meta">
                    <span className={`aii-pill ${section.key}`}>
                      {item.audits} audits
                    </span>
                    <button
                      type="button"
                      className="aii-link"
                      onClick={() => handleViewAudits(section, item)}
                    >
                      View audits <ArrowRightOutlined />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <AIObservationModal
        open={showModal}
        selectedQueId={1}
        onClose={handleCloseModal}
        title={getTitle({ type: modalType })}
      />
    </div>
  );
};

export default AIInsights;
